import { useEffect, useRef, useState } from 'react';
import { switchWorkspace } from '../lib/workspaceBoot';

const API = process.env.REACT_APP_API_URL || '';

/**
 * Header control for people who work in more than one workspace (TPA staff).
 * Shows the workspace currently open and lets the person move to another.
 * Renders nothing for everyone else.
 */
export default function WorkspaceSwitcher({ user }) {
  const count = user?.workspace?.workspaces_count || 1;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    let dead = false;
    fetch(`${API}/api/auth/workspaces`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (!dead) setItems(d.success ? d.workspaces : []); })
      .catch(() => { if (!dead) setItems([]); });
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => { dead = true; document.removeEventListener('mousedown', onDoc); };
  }, [open]);

  if (count <= 1) return null;

  const contract = user?.workspace?.cms_contract_number;

  const choose = async (ws) => {
    if (ws.organization_id === user.organization_id) { setOpen(false); return; }
    setBusy(ws.organization_id);
    setError(null);
    const r = await switchWorkspace(ws.organization_id);
    if (r.ok) { window.location.href = '/'; return; }
    setBusy(null);
    setError(r.message);
  };

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Choose the client you are working on"
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-700/70 hover:bg-slate-700 border border-slate-600 hover:border-slate-500 text-left transition"
      >
        <span className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0" />
        <span className="min-w-0">
          <span className="block text-[10px] uppercase tracking-wide text-slate-400 leading-none">Working in</span>
          <span className="block text-sm text-white font-medium leading-tight truncate max-w-[220px]">
            {user?.organization_name}{contract ? <span className="text-slate-400 font-normal"> · {contract}</span> : null}
          </span>
        </span>
        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-80 z-50 rounded-xl bg-slate-800 border border-slate-600 shadow-2xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-700 text-[11px] uppercase tracking-wide text-slate-400">
            Your workspaces
          </div>
          {items === null ? (
            <div className="px-4 py-4 text-sm text-slate-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-4 text-sm text-slate-400">No workspaces available.</div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {items.map((ws) => {
                const current = ws.organization_id === user.organization_id;
                return (
                  <li key={ws.organization_id}>
                    <button
                      type="button"
                      disabled={!!busy}
                      onClick={() => choose(ws)}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition ${
                        current ? 'bg-sky-500/10' : 'hover:bg-slate-700/60'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${current ? 'bg-sky-400' : 'bg-slate-600'}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-white truncate">{ws.organization_name}</span>
                        <span className="block text-xs text-slate-400">
                          {ws.cms_contract_number ? `${ws.cms_contract_number} · ` : ''}
                          {ws.is_home ? 'Your organization' : roleLabel(ws.user_type)}
                        </span>
                      </span>
                      {busy === ws.organization_id && <span className="text-xs text-slate-400">Opening…</span>}
                      {current && <span className="text-[10px] uppercase tracking-wide text-sky-300">Open</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {error && <div className="px-4 py-2 text-xs text-red-300 border-t border-slate-700">{error}</div>}
        </div>
      )}
    </div>
  );
}

function roleLabel(t) {
  return ({
    org_superadmin: 'Access administrator',
    compliance_officer: 'Compliance officer',
    compliance_analyst: 'Compliance analyst',
    department_lead: 'Department lead',
    department_user: 'Department user',
  })[t] || 'Member';
}
